import { FC, useMemo } from "react";
import {
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
  Box,
} from "components";
import { IDeleteUserModal } from "./types";
import { useCheckPermission } from "hooks";
import { FEATURES, FEATURE_ACTION } from "constants/features";
import { isTeamOwnerRow } from "./utils";

const DeleteUserModal: FC<IDeleteUserModal> = ({
  selectedRows,
  isLoading,
  isOpen,
  openDeleteModal,
  closeDeleteModal,
  handleSeatUnAssign,
}) => {
  const canEditUsers = useCheckPermission(
    FEATURE_ACTION.EDIT,
    FEATURES.MANAGE_USERS,
  );

  const handleCloseModal = () => {
    closeDeleteModal();
  };
  // Belt-and-braces: the table already excludes the owner from selection, so
  // this should never match. Kept so the button stays correct rather than
  // silently wrong if a caller ever feeds it an unfiltered selection.
  const hasOwnerSelected = useMemo(
    () => selectedRows.some(isTeamOwnerRow),
    [selectedRows],
  );

  return (
    <Box>
      <Button
        block
        onClick={() => {
          if (canEditUsers) {
            openDeleteModal();
          }
        }}
        variant="transparent"
        disabled={!selectedRows?.length || hasOwnerSelected}
      >
        <Icon name="trash" className="mr-2" /> Remove
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={handleCloseModal}
        label="UnAssign Seats Confirm Modal"
      >
        <ModalHeader
          className="max-w-lg mx-auto"
          title="Are you sure you want to unassign seats from selected users?"
        />
        <ModalBody className="mb-0">
          <div className="mt-16 flex flex-col md:flex-row items-center">
            <Button
              onClick={handleCloseModal}
              variant="black"
              block
              style={{ flex: 2 }}
            >
              No, Cancel
            </Button>
            {/* Cancel stays enabled on purpose — nothing aborts the request,
                so closing early is harmless, and disabling both would trap the
                user here if the request stalls. */}
            <Button
              variant="white"
              className="flex-1 ml-0 mt-3 md:ml-4 md:mt-0"
              onClick={handleSeatUnAssign}
              disabled={isLoading}
              block
            >
              {/* Default (black) spinner: this button is `variant="white"`, so
                  a white spinner was invisible against it. */}
              {isLoading ? <Spinner /> : "Yes, Proceed"}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </Box>
  );
};

export default DeleteUserModal;
